"use client";

import React, { memo, useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { FiSearch } from "@react-icons/all-files/fi/FiSearch";
import { FiX } from "@react-icons/all-files/fi/FiX";
import { FiArrowRight } from "@react-icons/all-files/fi/FiArrowRight";
import { FiGrid } from "@react-icons/all-files/fi/FiGrid";
import { FiStar } from "@react-icons/all-files/fi/FiStar";
import { FiLayout } from "@react-icons/all-files/fi/FiLayout";
import { FiClipboard } from "@react-icons/all-files/fi/FiClipboard";
import { FiRss } from "@react-icons/all-files/fi/FiRss";
import { FiMessageCircle } from "@react-icons/all-files/fi/FiMessageCircle";
import { FiImage } from "@react-icons/all-files/fi/FiImage";
import { FiClock } from "@react-icons/all-files/fi/FiClock";
import { FiAward } from "@react-icons/all-files/fi/FiAward";

import type { ILibraryCategory } from "../../helpers/widgetsLibraryData";
import type { ICatalogWebApp } from "../../helpers/widgetsCatalogWebData";
import { SocialProof } from "../socialProof/socialProof.comp";
import { Faq } from "../faq/faq.comp";
import { DEFAULT_PLATFORM_TILES } from "../widgetPlatformCarousel/widgetPlatformCarousel.data";
import { searchWidgets } from "../../helpers/widgetSearch";
import { StyledWidgetsCatalogWeb } from "./widgetsCatalogWeb.styles";

// The /widgets catalog page (DEV-15438), built from the "Widget Catalog:
// lavender option" board of https://claude.ai/artifact/TLdzhDcWi1fpoTGTUDqNgA.
// Started as a copy of widgetsLibraryV2 (the previous /widgets page, still
// used by /es/widgets and the platform pages): same hero, search, chips,
// steps and FAQ, with the board's cards, highlight banners and sticky search.
interface IWidgetsCatalogWebProps {
  apps: ICatalogWebApp[];
  categories: ILibraryCategory[];
}

type ILibraryApp = ICatalogWebApp;

const FIRST_PAGE = 48;
const PAGE_SIZE = 24;
const SUGGESTION_LIMIT = 8;
const FEATURED_TAGS = ["Popular", "Trending"];
// Where the faint platform marks sit in the hero background: left and right
// edges only, so nothing sits behind the headline or the search box.
const HERO_MARKS = [
  { x: 4, y: 14, size: 34, r: -8 },
  { x: 13, y: 34, size: 26, r: 6 },
  { x: 6, y: 56, size: 40, r: 4 },
  { x: 15, y: 74, size: 28, r: -6 },
  { x: 3, y: 88, size: 24, r: 10 },
  { x: 20, y: 12, size: 22, r: 4 },
  { x: 22, y: 90, size: 30, r: -4 },
  { x: 93, y: 12, size: 30, r: 8 },
  { x: 84, y: 30, size: 24, r: -6 },
  { x: 92, y: 50, size: 38, r: -4 },
  { x: 83, y: 70, size: 30, r: 6 },
  { x: 94, y: 86, size: 26, r: -10 },
  { x: 76, y: 10, size: 22, r: -4 },
  { x: 75, y: 90, size: 28, r: 6 },
];
// One line icon per category chip (design B, 2026-09-29).
const CATEGORY_ICONS: Record<string, React.ComponentType> = {
  "Reviews & trust": FiStar,
  "Popups & bars": FiLayout,
  "Forms & bookings": FiClipboard,
  "Social feeds": FiRss,
  "Chat & contact": FiMessageCircle,
  "Images & video": FiImage,
  "Timers & counters": FiClock,
  "Games & quizzes": FiAward,
};

// Category names used in links before the 2026-09-29 regrouping.
const LEGACY_CATEGORY_LINKS: Record<string, string> = {
  Reviews: "Reviews & trust",
  "Popups, Bars & Buttons": "Popups & bars",
  Forms: "Forms & bookings",
  "Social Feeds": "Social feeds",
  Chats: "Chat & contact",
  "Images & Galleries": "Images & video",
  Media: "Images & video",
  Gaming: "Games & quizzes",
};

// Highlight banners between the card rows of the unfiltered grid, after the
// 3rd card and then every 9 (the board's spacing). Copy from the board.
interface IHighlight {
  slug: string;
  name: string;
  style: "a" | "d";
  eyebrow?: string;
  tag: React.ReactNode;
  score?: { num: string; label: string };
  proof?: React.ReactNode;
}

const HIGHLIGHT_AFTER = [3, 12, 21, 30];
const HIGHLIGHTS: IHighlight[] = [
  {
    slug: "google-reviews",
    name: "Google Reviews",
    style: "a",
    eyebrow: "Most popular widget",
    tag: (
      <>
        Turn your Google reviews into your{" "}
        <span className="hl-grad">best salesperson</span>
      </>
    ),
    proof: (
      <>
        <span>
          <span className="hl-stars" aria-hidden="true">★★★★★</span>{" "}
          <strong>4.9</strong> on Google
        </span>
        <span className="hl-dot" />
        <span>
          Loved by <strong>500K+</strong> businesses
        </span>
      </>
    ),
  },
  {
    slug: "ai-chatbot",
    name: "AI Chatbot",
    style: "d",
    score: { num: "24/7", label: "Always-on chat support" },
    tag: "Support that never clocks out",
  },
  {
    slug: "instagram-feed",
    name: "Instagram Feed",
    style: "a",
    eyebrow: "Social feeds",
    tag: (
      <>
        Bring your Instagram <span className="hl-grad">to your website</span>
      </>
    ),
    proof: (
      <>
        <span>
          Loved by <strong>500K+</strong> businesses
        </span>
        <span className="hl-dot" />
        <span>
          <strong>Free</strong> plan on every widget
        </span>
      </>
    ),
  },
  {
    slug: "booking",
    name: "Appointment Booking",
    style: "d",
    score: { num: "1", label: "line of code to embed" },
    tag: "Turn visitors into booked appointments",
  },
];

const QUICK_SEARCHES = ["Google Reviews", "Instagram Feed", "Contact Form", "Countdown"];

const FAQS = [
  {
    q: "What is a website widget?",
    a: "A website widget is a small, self-contained feature you add to a page, like a reviews wall, an Instagram feed, a contact form or a countdown timer. You design it in the Common Ninja editor and paste one line of embed code where you want it to appear.",
  },
  {
    q: "Are Common Ninja widgets free?",
    a: "Yes. There is a free plan available for all our widgets, with no credit card required. Paid plans add premium features and remove limits when you need them.",
  },
  {
    q: "Which website platforms do the widgets work on?",
    a: "They work on 200+ platforms, including WordPress, Shopify, Wix, Squarespace, Webflow and plain HTML sites. If your site lets you paste embed code, you can add a Common Ninja widget.",
  },
  {
    q: "Do I need to know how to code?",
    a: "No. Every widget is customized visually in the editor: layout, colors, fonts and content. When it looks right, you copy the embed code and paste it into your site. Changes you make later update automatically.",
  },
  {
    q: "Can't find the widget you need?",
    a: 'Try <a href="https://embeddable.co?ref=commonninja" target="_blank">Embeddable</a>, our AI widget builder. Describe what you need and it builds a custom widget for you.',
  },
];

function badgeFor(app: ILibraryApp) {
  if (app.isNew) return "New";
  return app.tags.find((t) => FEATURED_TAGS.includes(t));
}

const categoryHref = (name: string) =>
  `/widgets?category=${encodeURIComponent(name)}`;

// Memoized: the grid holds every widget, so a parent re-render (typing in the
// search, toggling the sticky bar) must not redraw 239 cards.
const WidgetCard = memo(function WidgetCard({ app }: { app: ICatalogWebApp }) {
  const badge = badgeFor(app);
  return (
    <Link href={app.href} className="cg-card" title={`${app.name} widget`}>
      <div className={`cg-media ${app.wideImage ? "cg-fallback" : ""}`}>
        {badge && (
          <span className="cg-badge" data-kind={badge}>
            {badge}
          </span>
        )}
        <Image
          src={app.image}
          alt={`${app.name} widget preview`}
          width={480}
          height={360}
          loading="lazy"
        />
      </div>
      <div className="cg-body">
        <h3 className="cg-title">{app.name}</h3>
        <p className="cg-desc">{app.teaser}</p>
      </div>
    </Link>
  );
});

function Highlight({ h, slot, image }: { h: IHighlight; slot: number; image: string }) {
  const href = `/widgets/${h.slug}`;
  return (
    <div className="hl-slot" data-slot={slot} data-style={h.style}>
      <section className={`hl-${h.style}`} aria-label={`Featured widget: ${h.name}`}>
        <div>
          {h.eyebrow && <span className="hl-eyebrow">{h.eyebrow}</span>}
          {h.score && (
            <div className="hl-score">
              <span className="hl-num">{h.score.num}</span>
              <span className="hl-meta">
                <small>{h.score.label}</small>
              </span>
            </div>
          )}
          <h2 className="hl-name">{h.name}</h2>
          <p className="hl-tag">{h.tag}</p>
          <div className="hl-actions">
            <Link className="hl-btn hl-btn-primary" href={href}>
              Add {h.name} free <FiArrowRight aria-hidden="true" />
            </Link>
            <Link className="hl-btn hl-btn-ghost" href={`${href}/templates`}>
              See live demo
            </Link>
          </div>
          {h.proof && <div className="hl-proof">{h.proof}</div>}
        </div>
        <div className="hl-art">
          <Image src={image} alt={`${h.name} widget preview`} width={960} height={720} loading="lazy" />
        </div>
      </section>
    </div>
  );
}

export const WidgetsCatalogWeb = ({ apps, categories }: IWidgetsCatalogWebProps) => {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("");
  const [visible, setVisible] = useState(FIRST_PAGE);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [activeSuggestion, setActiveSuggestion] = useState(-1);
  const gridRef = useRef<HTMLElement>(null);
  const chipsSentinel = useRef<HTMLDivElement>(null);
  const chipsRef = useRef<HTMLElement>(null);

  // The chip bar is sticky, and so is the site header: it hides while you
  // scroll down and slides back in (0.3s) when you scroll up. So the bar sits
  // just under the header whenever the header is showing, and at the very top
  // otherwise. "Stuck" (the stronger floating look) means the sentinel above
  // the bar has scrolled past where the bar pins.
  // Everything here writes straight to the DOM, never to React state: a state
  // update per scroll frame re-rendered the whole grid and made scrolling
  // stutter. The bar's top jumps between two values and its CSS transition
  // matches the header's own slide, so nothing is measured mid-animation.
  useEffect(() => {
    const nav = chipsRef.current;
    const sentinel = chipsSentinel.current;
    if (!nav || !sentinel) return;
    let raf = 0;
    let top = -1;
    let stuck: boolean | null = null;
    const headerOffset = () => {
      let offset = 0;
      document.querySelectorAll<HTMLElement>("header.visible").forEach((h) => {
        if (h.offsetHeight === 0) return; // display: none at this width
        const cs = window.getComputedStyle(h);
        if (cs.position !== "fixed") return;
        offset = Math.max(
          offset,
          Math.round(h.offsetHeight + parseFloat(cs.marginTop || "0")) + 8
        );
      });
      return offset;
    };
    const measure = () => {
      raf = 0;
      const offset = headerOffset();
      if (offset !== top) {
        top = offset;
        nav.style.top = `${offset}px`;
      }
      const nextStuck = sentinel.getBoundingClientRect().top < offset;
      if (nextStuck === stuck) return;
      // The stuck bar has a different height from the resting one (chips stop
      // wrapping, the search field joins). Absorb the difference in its
      // bottom margin so the cards below do not jump when it sticks.
      const restingHeight = stuck ? 0 : nav.offsetHeight;
      stuck = nextStuck;
      nav.classList.toggle("is-stuck", stuck);
      nav.style.marginBottom = stuck
        ? `${restingHeight - nav.offsetHeight}px`
        : "";
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(measure);
    };
    measure();
    // The header shows/hides by toggling its class, and remounts when it
    // switches between its top and floating versions.
    const observer = new MutationObserver(onScroll);
    observer.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ["class"],
    });
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      cancelAnimationFrame(raf);
    };
  }, []);
  const urlReady = useRef(false);

  // Read ?search= / ?category= after mount, so the static HTML always holds
  // the full, unfiltered list of widget links for crawlers.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const s = params.get("search") || "";
    const c = params.get("category") || "";
    // Old ?category= links use the DB category names; send them to the group
    // that replaced them. Anything unknown falls back to "All".
    const name = LEGACY_CATEGORY_LINKS[c] || c;
    const match = categories.find(
      (cat) => cat.name.toLowerCase() === name.toLowerCase()
    );
    if (s) setQuery(s);
    if (match) setCategory(match.name);
  }, [categories]);

  useEffect(() => {
    // Skip the first run, so the effect above reads the URL before it is rewritten.
    if (!urlReady.current) {
      urlReady.current = true;
      return;
    }
    const params = new URLSearchParams(window.location.search);
    if (query.trim()) params.set("search", query.trim());
    else params.delete("search");
    if (category) params.set("category", category);
    else params.delete("category");
    const qs = params.toString();
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}${qs ? `?${qs}` : ""}`
    );
    setVisible(FIRST_PAGE);
  }, [query, category]);

  // The empty state suggests the widgets the site already badges Popular.
  const popular = useMemo(
    () => apps.filter((a) => a.tags.includes("Popular")).slice(0, 3),
    [apps]
  );

  // Typing updates the input at once; the grid and suggestions follow a beat
  // behind (React deferred value), so keystrokes never wait on 239 cards.
  const deferredQuery = useDeferredValue(query);
  const matches = useMemo(
    () => (deferredQuery.trim().length > 1 ? searchWidgets(apps, deferredQuery) : []),
    [apps, deferredQuery]
  );
  const suggestions = matches.slice(0, SUGGESTION_LIMIT);

  // Keep the highlighted suggestion visible when arrowing through the list.
  useEffect(() => {
    if (activeSuggestion < 0) return;
    document
      .getElementById(`widget-suggestion-${activeSuggestion}`)
      ?.scrollIntoView({ block: "nearest" });
  }, [activeSuggestion]);

  const results = useMemo(() => {
    const inCategory = category
      ? apps.filter((a) => a.categories.includes(category))
      : apps;
    return deferredQuery.trim() ? searchWidgets(inCategory, deferredQuery) : inCategory;
  }, [apps, deferredQuery, category]);

  const isFiltered = Boolean(deferredQuery.trim() || category);
  const shown = Math.min(visible, results.length);

  // Picking a category or searching hides the highlight banners, which moves
  // the cards around. So only ask for the scroll here, and do it after React
  // has drawn the new layout.
  const [scrollRequest, setScrollRequest] = useState(0);
  useEffect(() => {
    if (!scrollRequest) return;
    gridRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [scrollRequest]);

  function scrollToGrid() {
    setScrollRequest((n) => n + 1);
  }

  function pickCategory(e: React.MouseEvent, name: string) {
    e.preventDefault();
    setCategory(name);
    setQuery("");
    scrollToGrid();
  }

  function runSearch(term: string) {
    setQuery(term);
    setCategory("");
    setSuggestOpen(false);
    scrollToGrid();
  }

  function onSearchKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!suggestOpen || !suggestions.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveSuggestion((i) => (i + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveSuggestion((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === "Enter" && activeSuggestion >= 0) {
      e.preventDefault();
      window.location.href = suggestions[activeSuggestion].href;
    } else if (e.key === "Escape") {
      setSuggestOpen(false);
    }
  }

  const gridTitle = category
    ? `${category} widgets`
    : query.trim()
    ? `Results for “${query.trim()}”`
    : "All widgets";

  // Every widget stays in the HTML as a real link (later pages are hidden with
  // CSS), so crawlers still see all of them. Banners only go between cards
  // that are showing, and only in the unfiltered view.
  const gridItems = useMemo(() => {
    const heroOf = (slug: string) =>
      apps.find((a) => a.href === `/widgets/${slug}`)?.image || "";
    const items: React.ReactNode[] = [];
    results.forEach((app, i) => {
      const slot = HIGHLIGHT_AFTER.indexOf(i);
      if (!isFiltered && slot >= 0 && i < visible) {
        const h = HIGHLIGHTS[slot];
        items.push(
          <Highlight key={`hl-${h.slug}`} h={h} slot={slot + 1} image={heroOf(h.slug)} />
        );
      }
      items.push(
        <div key={app._id} className={i >= visible ? "is-hidden" : undefined}>
          <WidgetCard app={app} />
        </div>
      );
    });
    return items;
  }, [apps, results, isFiltered, visible]);

  return (
    <StyledWidgetsCatalogWeb>
      <section className="hero">
        {/* Faint platform marks around the edges: the "any platform" line,
            said quietly by the background. Decorative only. */}
        <div className="hero-marks" aria-hidden="true">
          {HERO_MARKS.map((m, i) => {
            const p = DEFAULT_PLATFORM_TILES[i % DEFAULT_PLATFORM_TILES.length];
            return (
              <svg
                key={p.slug}
                viewBox="0 0 24 24"
                style={{
                  left: `${m.x}%`,
                  top: `${m.y}%`,
                  width: m.size,
                  height: m.size,
                  transform: `rotate(${m.r}deg)`,
                }}
              >
                <path d={p.iconPath} />
              </svg>
            );
          })}
        </div>
        <div className="hero-inner">
          <div className="hero-proof">
            <SocialProof avatarSize="small" />
          </div>
          <h1>
            Free <span className="gradient">website widgets</span>.{" "}
            <span className="second-line">Any platform, zero code.</span>
          </h1>
          <p className="lead">
            Show off reviews, collect leads, take bookings and sell more with
            ready-made widgets. Make any widget match your brand in the visual
            editor, then paste one line of code on WordPress, Shopify, Wix or
            any other website.
          </p>

          <form
            className="search"
            role="search"
            onSubmit={(e) => {
              e.preventDefault();
              runSearch(query);
            }}
          >
            <FiSearch className="search-icon" aria-hidden />
            <input
              type="search"
              role="combobox"
              aria-label="Search widgets"
              aria-autocomplete="list"
              aria-expanded={suggestOpen && suggestions.length > 0}
              aria-controls="widget-suggestions"
              placeholder="Search widgets, e.g. reviews, form, countdown"
              value={query}
              autoComplete="off"
              onChange={(e) => {
                setQuery(e.target.value);
                setCategory("");
                setSuggestOpen(true);
                setActiveSuggestion(-1);
              }}
              onFocus={() => setSuggestOpen(true)}
              onBlur={() => setTimeout(() => setSuggestOpen(false), 150)}
              onKeyDown={onSearchKey}
            />
            <button type="submit">Search</button>
            {suggestOpen && suggestions.length > 0 && (
              <div className="suggest-panel">
                <ul className="suggestions" id="widget-suggestions" role="listbox">
                  {suggestions.map((app, i) => (
                    <li
                      key={app._id}
                      id={`widget-suggestion-${i}`}
                      role="option"
                      aria-selected={i === activeSuggestion}
                    >
                      <Link href={app.href}>
                        <Image src={app.logo} alt="" width={28} height={28} />
                        <span>
                          <strong>{app.name}</strong>
                          <small>{app.categories[0]}</small>
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
                {matches.length > suggestions.length && (
                  <button
                    type="button"
                    className="suggest-all"
                    // mousedown fires before the input's blur closes the panel
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => runSearch(query)}
                  >
                    See all {matches.length} results <FiArrowRight />
                  </button>
                )}
              </div>
            )}
          </form>

          <div className="quick">
            <span>Popular searches:</span>
            {QUICK_SEARCHES.map((term) => (
              <button key={term} type="button" onClick={() => runSearch(term)}>
                {term}
              </button>
            ))}
          </div>

          <div className="scores">
            <span>
              <span className="star" aria-hidden="true">★</span> <strong>4.9</strong> on Google
            </span>
            <span className="dot" aria-hidden="true">·</span>
            <span>
              <span className="star" aria-hidden="true">★</span> <strong>4.3</strong> on Trustpilot
            </span>
            <span className="dot" aria-hidden="true">·</span>
            <span>Free plan on every widget</span>
          </div>
        </div>
      </section>

      <div ref={chipsSentinel} aria-hidden="true" />
      {/* is-stuck, top and margin-bottom are set by the scroll effect above. */}
      <nav className="chips" aria-label="Widget categories" ref={chipsRef}>
        <div className="chips-track">
          {/* Shows once the bar sticks, so search stays in reach after the
              hero has scrolled away. Same query as the hero search. */}
          <form
            className="chips-search"
            role="search"
            onSubmit={(e) => {
              e.preventDefault();
              runSearch(query);
            }}
          >
            <FiSearch aria-hidden />
            <input
              type="search"
              aria-label="Search widgets"
              placeholder={`Search ${apps.length} widgets`}
              value={query}
              autoComplete="off"
              onChange={(e) => {
                setQuery(e.target.value);
                setCategory("");
              }}
            />
          </form>
          <a
            href="/widgets"
            className={`chip ${!category ? "active" : ""}`}
            onClick={(e) => pickCategory(e, "")}
          >
            <FiGrid aria-hidden="true" /> All
          </a>
          {categories.map((cat) => {
            const Icon = CATEGORY_ICONS[cat.name] || FiGrid;
            return (
              <a
                key={cat.name}
                href={categoryHref(cat.name)}
                className={`chip ${category === cat.name ? "active" : ""}`}
                onClick={(e) => pickCategory(e, cat.name)}
              >
                <Icon aria-hidden="true" /> {cat.name}
              </a>
            );
          })}
        </div>
      </nav>

      <main className="main">
        <section className="row" ref={gridRef} id="all-widgets">
          <div className="row-head row-head-split">
            <div>
              <h2>{gridTitle}</h2>
              <p aria-live="polite">
                {results.length
                  ? `Showing ${shown} of ${results.length} widgets`
                  : "No matching widgets"}
              </p>
            </div>
            {isFiltered && (
              <button
                type="button"
                className="clear"
                onClick={() => {
                  setQuery("");
                  setCategory("");
                }}
              >
                Clear filters <FiX />
              </button>
            )}
          </div>

          {results.length > 0 ? (
            <div className="cg-grid">{gridItems}</div>
          ) : (
            <div className="empty">
              <h3>No widget matches “{query.trim()}” yet</h3>
              <p>Try one of these categories, or start from a popular widget.</p>
              <div className="empty-chips">
                {categories.slice(0, 6).map((cat) => (
                  <a
                    key={cat.name}
                    href={categoryHref(cat.name)}
                    className="chip"
                    onClick={(e) => pickCategory(e, cat.name)}
                  >
                    {cat.name}
                  </a>
                ))}
              </div>
              <div className="cg-grid">
                {popular.map((app) => (
                  <WidgetCard key={app._id} app={app} />
                ))}
              </div>
              <p className="empty-embd">
                Need something custom?{" "}
                <Link href="https://embeddable.co?ref=commonninja" target="_blank">
                  Build it with Embeddable, our AI widget builder
                </Link>
              </p>
            </div>
          )}

          {results.length > visible && (
            <div className="more">
              <button type="button" onClick={() => setVisible((v) => v + PAGE_SIZE)}>
                Show {Math.min(PAGE_SIZE, results.length - visible)} more widgets
              </button>
            </div>
          )}
        </section>

        <section className="steps">
          <h2>
            Pick, customize, <span className="gradient">embed</span>
          </h2>
          <ol>
            <li>
              <span className="step-num">1</span>
              <strong>Pick a widget</strong>
              <p>Browse the library and start from a ready-made template.</p>
            </li>
            <li>
              <span className="step-num">2</span>
              <strong>Make it yours</strong>
              <p>Change the layout, colors, fonts and content in the visual editor. No code needed.</p>
            </li>
            <li>
              <span className="step-num">3</span>
              <strong>Embed it anywhere</strong>
              <p>Paste one line of code on any of 200+ platforms. Later edits update on their own.</p>
            </li>
          </ol>
          <Link href="/signup" className="steps-cta">
            Start for Free <FiArrowRight />
          </Link>
          <p className="steps-note">Free forever. No credit card required.</p>
        </section>

        <section className="faq-wrap">
          <Faq faqs={FAQS} />
        </section>
      </main>
    </StyledWidgetsCatalogWeb>
  );
};
